import type { ArticleBlock, LearnArticle, LearnAudience, LearnTopic } from '../types/learnTypes'
import { beforeListingArticles } from '../content/beforeListingArticles'
import { afterListingArticles } from '../content/afterListingArticles'

// Read-side helpers over the static article set. This file is the seam a CMS
// would replace: every consumer goes through these functions rather than
// importing a content file, so swapping the source for a query hook later
// touches this file only.

const articles: LearnArticle[] = [...beforeListingArticles, ...afterListingArticles]

export function allArticles(): LearnArticle[] {
  return articles
}

export function articlesFor(audience: LearnAudience): LearnArticle[] {
  return articles.filter((article) => article.audience === audience)
}

export function findArticleBySlug(slug: string): LearnArticle | undefined {
  return articles.find((article) => article.slug === slug)
}

// The other reads in the same audience — what the article page offers next.
export function siblingArticles(article: LearnArticle): LearnArticle[] {
  return articlesFor(article.audience).filter((sibling) => sibling.slug !== article.slug)
}

// Row data for a dashboard card. The card gets the article's own title and lede
// so the two can never describe the same read differently.
export function topicsFor(audience: LearnAudience): LearnTopic[] {
  return articlesFor(audience).map((article) => ({
    slug: article.slug,
    title: article.title,
    subtitle: article.lede,
    icon: article.icon,
  }))
}

function blockText(block: ArticleBlock): string[] {
  switch (block.kind) {
    case 'paragraph':
    case 'subheading':
    case 'closing':
      return [block.text]
    case 'bullets':
      return [
        ...(block.intro ? [block.intro] : []),
        ...block.items.flatMap((item) => (item.lead ? [item.lead, item.text] : [item.text])),
      ]
    case 'steps':
      return block.items.flatMap((item) => [item.label, item.title, item.text])
    case 'comparison':
      return block.sides.flatMap((side) => [side.label, side.text])
  }
}

// Whole-article haystacks, built once at import. Search covers the body, not
// just the title: a seller looking for "VARA" or "title deed" is searching for a
// term they read somewhere, not for a headline they remember.
const searchIndex = new Map<string, string>(
  articles.map((article) => [
    article.slug,
    [article.title, article.lede, ...article.blocks.flatMap(blockText)].join(' ').toLowerCase(),
  ]),
)

// Every whitespace-separated term must appear somewhere in the article, in any
// order — so "dubai market" finds the market guide without the seller having to
// reproduce the phrasing. An empty query returns everything rather than nothing.
export function searchArticles(query: string): LearnArticle[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (!terms.length) return articles

  return articles.filter((article) => {
    const haystack = searchIndex.get(article.slug) ?? ''
    return terms.every((term) => haystack.includes(term))
  })
}
